import React, { useState } from 'react';  
import { motion } from 'framer-motion';

const projects = [
  {
    title: '🧠 FocusWin: Productivity-First To-Do & Analytics',
    tech: ['⚛️ React', '🌐 Node.js', '🍃 MongoDB', '📩 Notifications', '🧮 Custom Algorithms'],
    image: 'ToDo.png',
    description:
      'FocusWin is a powerful productivity app - not just a to-do list. With smart dashboards, analytics, task rankings, and motivation tools, it helps you track, analyze, and improve your daily workflow.',
    features: [
      '📊 Interactive dashboard with analytics & heatmaps',
      '🗂️ Smart task grouping + importance ranking (1–10)',
      '✅ Visual progress tracking & insights',
      '📬 Email + push reminders',
      '🧠 Productivity scoring with custom logic',
      '🔥 Streaks, points, and achievements',
    ],
    github: 'https://github.com/pavithra2870/FocusWin',
    frontend: 'https://github.com/pavithra2870/FocusWin---Frontend',
    images: ['/HomeTO.png','/ToDo.png','/Todof.jpg','/todoff.jpg','/Todo_ board.jpg','/Todo_map.jpg'],
  },
  {
    title: '🌸 SafeSpace: AI Mood Journal & Companion',
    tech: ['⚛️ React', '🧠 Groq API', '🤖 GenAI', '🎨 Modern UI/UX'],
    image: 'M1.png',
    description:
      'SafeSpace is a cozy, AI-powered journal for emotional wellness. With mood tracking, affirmations, and a calming interface, it helps users reflect, feel supported, and stay motivated.',
    features: [
      '📝 Mood journaling with AI support (Ms. Luna)',
      '🌟 Daily affirmations & manifestation tools',
      '📈 Mood trends: heatmaps, calendar, streaks',
      '🧘‍♀️ Tips & emotional well-being analysis',
      '🌍 Supportive community board',
      '🎀 Soft pink, calming UI',
    ],
    github: 'https://github.com/pavithra2870/SafeSpace---Journal',
    images: [ '/Home.png', '/GetStarted.png','/Community.png','/Dashboard.png', '/M1.png', '/D2.png','/F2.png','/F3.png','Insights.png','/Journals.png',],
  }
];

const ProjectSlideshow = ({ images }) => {
  const [slideIdx, setSlideIdx] = useState(0);
  const totalSlides = images ? images.length : 0;
  const handlePrev = () => setSlideIdx((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  const handleNext = () => setSlideIdx((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  if (!images || totalSlides === 0) return null;
  return (
    <div className="flex flex-col items-center mb-10">
      <div className="relative">
        <img
          src={images[slideIdx]}
          alt="Project screenshot"
          className="w-[500px] h-[320px] object-cover rounded-xl shadow-2xl border-4 border-pink-300"
        />
        <button onClick={handlePrev} className="absolute left-0 top-1/2 -translate-y-1/2 bg-pink-400 text-white px-4 py-2 rounded-l-xl hover:bg-pink-500 text-2xl">&#8592;</button>
        <button onClick={handleNext} className="absolute right-0 top-1/2 -translate-y-1/2 bg-pink-400 text-white px-4 py-2 rounded-r-xl hover:bg-pink-500 text-2xl">&#8594;</button>
      </div>
      <div className="mt-3 text-base text-gray-400 font-semibold">{slideIdx + 1} / {totalSlides}</div>
    </div>
  );
};

const ProjectCard = ({ project, idx }) => {
  return (
    <motion.div
      key={project.title}
      className="bg-gray-900 rounded-lg p-6 text-left flex flex-col justify-between h-full transition transform hover:scale-105"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 * idx }}
      viewport={{ once: true }}
    >
      <div>
        <h3 className="text-xl font-bold mb-2 text-pink-400 transition hover:underline hover:text-pink-300 cursor-pointer">{project.title}</h3>
        <div className="mb-2 text-sm text-gray-300">{project.tech.join(', ')}</div>
        <p className="mb-3 text-gray-200">{project.description}</p>
        <ul className="list-disc list-inside mb-3 text-gray-300">
          {project.features.map((feature) => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
      </div>
      <div className="flex gap-4 mt-2">
        {project.frontend && (
          <a href={project.frontend} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Frontend GitHub</a>
        )}
        <a href={project.github} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800">GitHub</a>
      </div>
    </motion.div>
  );
};

const Projects = () => (
  <section className="py-12 bg-black text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <motion.div
      className="max-w-3xl mx-auto px-4"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <motion.h2
        className="text-3xl font-bold mb-4 text-purple-400"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        viewport={{ once: true }}
      >
        Projects
      </motion.h2>
      {projects.map((project, idx) => (
        <div key={project.title}>
          <ProjectSlideshow images={project.images} />
          <ProjectCard project={project} idx={idx} />
        </div>
      ))}
    </motion.div>
  </section>
);

export default Projects; 